import React from 'react';
import { useAuth } from '../../App';
import { Seo } from '../../components/seo/Seo';
import { PublicLayout } from '../../components/public/PublicLayout';
import { Container, FeatureGrid, CtaBand } from '../../components/public/PublicSections';
import { SectionHeading } from '../../components/public/SectionHeading';
import { FEATURES, FEATURES_PAGE, HOME, LAYOUT_COPY } from '../../seo/publicContent';
import { pageMeta, pagePath, alternatesFor } from '../../seo/seoConfig.mjs';
import { breadcrumbSchema, softwareSchema } from '../../seo/structuredData';

export default function PublicFeaturesPage({ lang }) {
  const { user } = useAuth();
  const copy = FEATURES_PAGE[lang];
  const meta = pageMeta('features', lang);
  const appHref = user ? (user.onboarding_complete ? '/dashboard' : '/onboarding') : '/auth';

  return (
    <PublicLayout lang={lang} pageKey="features">
      <Seo
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        path={pagePath('features', lang)}
        lang={lang}
        alternates={alternatesFor('features')}
        jsonLd={[
          breadcrumbSchema([
            { name: LAYOUT_COPY[lang].nav.home, path: pagePath('home', lang) },
            { name: LAYOUT_COPY[lang].nav.features, path: pagePath('features', lang) },
          ]),
          softwareSchema(lang),
        ]}
      />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ backgroundImage: 'radial-gradient(600px circle at 15% 0%, rgba(243,179,61,0.12), transparent 55%)' }} />
        <Container className="relative pt-14 pb-12 sm:pt-20 sm:pb-16">
          <SectionHeading as="h1" eyebrow={copy.eyebrow} title={copy.h1} sub={copy.sub} testId="features-page-heading" />
        </Container>
      </section>

      <section className="pb-16 sm:pb-24" aria-label={copy.eyebrow}>
        <Container>
          <FeatureGrid features={FEATURES} lang={lang} detailed testId="features-detailed-grid" />
        </Container>
      </section>

      <section className="py-16 sm:py-24 border-t border-white/[0.06]" aria-labelledby="method-title">
        <Container>
          <SectionHeading title={copy.methodTitle} testId="method-heading" />
          <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="method-list">
            {copy.method.map(step => (
              <li key={step.n} className="rounded-2xl border border-white/10 bg-lux-panel/90 p-5">
                <span className="font-display text-3xl text-[var(--lux-gold)] tabular-nums">{step.n}</span>
                <h3 className="mt-3 text-base font-semibold text-white">{step.title}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{step.text}</p>
              </li>
            ))}
          </ol>
        </Container>
      </section>

      <CtaBand title={copy.finalTitle} sub={copy.finalSub} ctaLabel={HOME[lang].ctaPrimary} to={appHref} testId="features-final-cta" />
    </PublicLayout>
  );
}
